import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import CountdownTimer from "../components/CountdownTimer";
import DocumentScoreCard from "../components/DocumentScoreCard";
import {
  ArrowLeft,
  MapPin,
  TrendingUp,
  Coins,
  DollarSign,
  Shield,
  Calendar,
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  Building,
  Users,
  BarChart3,
  Rocket,
  FileCheck
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [investModal, setInvestModal] = useState(false);
  const [investAmount, setInvestAmount] = useState(986);

  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    try {
      const [projectRes, docsRes] = await Promise.all([
        axios.get(`${API}/projects/${id}`),
        axios.get(`${API}/projects/${id}/documents`)
      ]);
      setProject(projectRes.data);
      setDocuments(docsRes.data);
    } catch (error) {
      console.error("Failed to fetch project:", error);
      toast.error("Project not found");
      navigate("/projects");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleInvest = async () => {
    if (project.status === "launching") {
      toast.error("This project hasn't launched yet. Please wait for the launch date.");
      return;
    }
    
    const tokens = Math.floor(investAmount / project.token_price);
    if (tokens < 1) {
      toast.error("Minimum investment is 1 token");
      return;
    }

    try {
      await axios.post(`${API}/investments?project_id=${project.id}&tokens=${tokens}`);
      toast.success(`Successfully invested ${formatCurrency(investAmount)} for ${tokens} tokens!`);
      setInvestModal(false);
      fetchProject();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process investment");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-5 h-5 text-emerald-600" />;
      case "in_progress":
        return <Clock className="w-5 h-5 text-amber-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-slate-400" />;
    }
  };

  const getScoreColor = (score) => {
    if (score >= 800) return "text-emerald-600";
    if (score >= 600) return "text-blue-600";
    if (score >= 400) return "text-amber-600";
    return "text-red-600";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!project) return null;

  const score = project.score_data?.score || 0;
  const tokensFromInvestment = Math.floor(investAmount / project.token_price);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="project-detail-page">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate("/projects")}
        className="text-slate-600"
        data-testid="back-to-projects"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Projects
      </Button>

      {/* Hero Section */}
      <div className="relative rounded-xl overflow-hidden">
        <img
          src={project.image_url}
          alt={project.name}
          className="w-full h-64 object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="industry-tag bg-white/20 text-white border-white/30">{project.industry}</span>
            <span className="bg-[#0F2744] text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
              <Rocket className="w-3 h-3" />
              Launching Soon
            </span>
            <span className={`px-3 py-1 rounded-full text-sm font-bold ${getScoreColor(score)} bg-white`}>
              Score: {score}/1000
            </span>
          </div>
          <h1 className="text-3xl font-bold text-white">{project.name}</h1>
          <div className="flex items-center gap-4 mt-2 text-white/80">
            <span className="flex items-center">
              <MapPin className="w-4 h-4 mr-1" />
              {project.location}
            </span>
            <span className="font-mono text-sm">{project.token_symbol}</span>
          </div>
        </div>
      </div>

      {/* Launch Countdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <CountdownTimer launchDate={project.launch_date} projectName={project.name} />
        </div>
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="text-center">
              <p className="text-sm text-slate-500">Document Score</p>
              <p className={`text-5xl font-bold mt-2 ${getScoreColor(score)}`}>{score}</p>
              <p className="text-slate-400">/1000</p>
              <div className="mt-4">
                <Progress value={(score / 1000) * 100} className="h-3" />
              </div>
              <p className="text-sm text-slate-500 mt-2">
                {project.score_data?.completion_percentage || 0}% documents approved
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card className="border-slate-200" data-testid="project-description">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900">Project Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 leading-relaxed">{project.description}</p>
            </CardContent>
          </Card>

          {/* Tabs */}
          <Tabs defaultValue="documents" className="space-y-4">
            <TabsList className="bg-slate-100">
              <TabsTrigger value="documents" className="text-slate-900 data-[state=active]:bg-white data-[state=active]:text-slate-900" data-testid="tab-documents">Documents</TabsTrigger>
              <TabsTrigger value="overview" className="text-slate-900 data-[state=active]:bg-white data-[state=active]:text-slate-900" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="milestones" className="text-slate-900 data-[state=active]:bg-white data-[state=active]:text-slate-900" data-testid="tab-milestones">Milestones</TabsTrigger>
            </TabsList>

            <TabsContent value="documents">
              <DocumentScoreCard 
                scoreData={project.score_data} 
                documents={documents}
                showDetails={true}
              />
            </TabsContent>

            <TabsContent value="overview">
              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Token Supply</p>
                      <p className="text-xl font-bold text-slate-900">{project.token_supply.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Token Price</p>
                      <p className="text-xl font-bold text-slate-900">{formatCurrency(project.token_price)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Funding Goal</p>
                      <p className="text-xl font-bold text-slate-900">{formatCurrency(project.funding_goal)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Expected ROI</p>
                      <p className="text-xl font-bold text-emerald-600">{project.roi_percentage}%</p>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-slate-100">
                    <h4 className="font-medium text-slate-900 mb-4">Project Highlights</h4>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <Building className="w-5 h-5 text-[#3B82F6] mt-0.5" />
                        <div>
                          <p className="font-medium text-slate-900">Industry: {project.industry}</p>
                          <p className="text-sm text-slate-500">Tokenized {project.industry.toLowerCase()} investment</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-[#3B82F6] mt-0.5" />
                        <div>
                          <p className="font-medium text-slate-900">Escrow Protected</p>
                          <p className="text-sm text-slate-500">Funds released based on milestone completion</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <FileCheck className="w-5 h-5 text-[#3B82F6] mt-0.5" />
                        <div>
                          <p className="font-medium text-slate-900">AI-Scored Due Diligence</p>
                          <p className="text-sm text-slate-500">38 documents validated for investor confidence</p>
                        </div>
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="milestones">
              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <h4 className="font-medium text-slate-900 mb-4">Project Milestones</h4>
                  <div className="space-y-4">
                    {project.milestones?.map((milestone, idx) => (
                      <div
                        key={idx}
                        className={`p-4 rounded-lg border ${
                          milestone.status === "completed" ? "bg-emerald-50 border-emerald-200" :
                          milestone.status === "in_progress" ? "bg-amber-50 border-amber-200" :
                          "bg-white border-slate-200"
                        }`}
                        data-testid={`milestone-${idx}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {getStatusIcon(milestone.status)}
                            <div>
                              <p className="font-medium text-slate-900">{milestone.name}</p>
                              <p className="text-sm text-slate-500">Target: {formatCurrency(milestone.target)}</p>
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            milestone.status === "completed" ? "bg-emerald-100 text-emerald-700" :
                            milestone.status === "in_progress" ? "bg-amber-100 text-amber-700" :
                            "bg-slate-100 text-slate-600"
                          }`}>
                            {milestone.status.replace("_", " ")}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar - Investment Card */}
        <div className="space-y-6">
          <Card className="border-slate-200 sticky top-6" data-testid="investment-card">
            <CardHeader className="bg-[#0F2744] text-white rounded-t-lg">
              <CardTitle className="text-lg">Investment Details</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Token Price</span>
                <span className="text-2xl font-bold text-slate-900">{formatCurrency(project.token_price)}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-slate-600">Token Supply</span>
                <span className="font-semibold text-slate-900">{project.token_supply.toLocaleString()}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-slate-600">Expected ROI</span>
                <span className="font-semibold text-emerald-600 flex items-center">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  {project.roi_percentage}%
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-slate-600">Document Score</span>
                <span className={`font-semibold ${getScoreColor(score)}`}>
                  {score}/1000
                </span>
              </div>

              <div className="pt-4 border-t border-slate-100">
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 mb-4">
                  <div className="flex items-center gap-2 text-amber-800">
                    <Clock className="w-5 h-5" />
                    <span className="font-medium">Launching Soon</span>
                  </div>
                  <p className="text-sm text-amber-700 mt-1">
                    Investment will be available after launch
                  </p>
                </div>

                <Button
                  onClick={() => setInvestModal(true)}
                  className="w-full bg-slate-300 text-slate-500 cursor-not-allowed h-12"
                  disabled
                  data-testid="invest-now-btn"
                >
                  <Rocket className="w-4 h-4 mr-2" />
                  Available After Launch
                </Button>
              </div>

              <div className="pt-4 border-t border-slate-100 text-center">
                <p className="text-xs text-slate-500">
                  Minimum investment: {formatCurrency(project.token_price)}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Launch Date</span>
                  <span className="font-medium text-slate-900 flex items-center">
                    <Calendar className="w-4 h-4 mr-1 text-slate-400" />
                    {new Date(project.launch_date).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Documents Approved</span>
                  <span className="font-medium text-slate-900">
                    {project.score_data?.approved_documents || 0}/{project.score_data?.total_documents || 0}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Investment Modal */}
      <Dialog open={investModal} onOpenChange={setInvestModal}>
        <DialogContent className="max-w-md" data-testid="invest-modal">
          <DialogHeader>
            <DialogTitle>Invest in {project.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-amber-800 font-medium">Project Not Yet Launched</p>
              <p className="text-sm text-amber-700 mt-1">
                This project will launch on {new Date(project.launch_date).toLocaleDateString()}.
                Investment will be available after launch.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvestModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
